import { validateSequences } from './ValidateSequences';
import { ValidatorErrorTypes } from "../constants";
import { ValidationError } from './ValidationError';

// TS Placeholders
type ConfigManager = any; // vse/machinelogic/ConfigManager.js
type Application = any; // vse/machinelogic/Application.js
type Instruction = any; // vse/machinelogic/Instruction.js


export function validateOneFsmLimit(pApp: Application): ValidationError[] {
  const lInitialSequence = pApp.getInitialSequence();
  if(!lInitialSequence) {
    return [];
  }
  const lFsmInstructions = lInitialSequence.listInstructions().filter((pInstruction) => pInstruction.mName == 'fsm');

  if(lFsmInstructions.length <= 1) {
    return [];
  }

  return [
    {
      header: `More than one State Machine command in application`,
      onError: {
        message: "It is recommended to only have one State Machine command per application.",
        options: [{ message: 'Remove all additional State Machine commands', target: 'none' }]
      },
      onSuccess: {
        message: "Application only has one State Machine command."
      },
      type: ValidatorErrorTypes.ApplicationError,
      key: 'oneStateMachineLimit',
      app: pApp.mName,
      seq: pApp.mInitialSequence.mName,
      id: pApp.mId
    }
  ];
}


interface validateTransitionStateInputsParams {
  configManager: ConfigManager;
  sequenceName: string;
  instruction: Instruction;
  instructionIndex: number;
}

/**
 * Handle missing inputs
 */
function validateTransitionStateInputs({configManager, sequenceName, instruction, instructionIndex}: validateTransitionStateInputsParams): ValidationError[] {
  const lAllConfiguredInputUuids = configManager.listInputs().map(pInput => pInput.getUUID());
  const lStates = instruction.getParameter('states');

  const lTransitionStatesMissingInputs = lStates
    .map(state => state.targetStates.map(targetState => ({state, targetState})))
    .flat()
    .filter((pState) => {
      const lInputUuid = pState.targetState.eventDefinition.split('|')[0];
      return pState.targetState.eventType === 'digital_input' && !lAllConfiguredInputUuids.includes(lInputUuid);
    });

  return lTransitionStatesMissingInputs.map(({targetState, state}) => {
    const lOptions = [{message: `Add an Input in the Configuration tab.`, target: 'redirect-config'}];
    if (configManager.listInputs().length) {
      lOptions.push({ message: `Select an Input from the drop down menu in the "${targetState.stateName}" modal.`, target: 'none' }); // currently not handling highlighting the input select element in the modal
    }

    return {
      header: 'Missing associated Input.',
      onError: {
        message: `"${sequenceName}" → command #${instructionIndex}, missing input in the state transition from state "${state.stateName}" to state "${targetState.stateName}" .`,
        options: lOptions
      },
      onSuccess: {
        message: 'You do not have any missing Input.'
      },
      instructionSubResource: state
    };
  });
}

interface validateTransitionStateReferenceParams {
  sequenceName: string;
  instruction: Instruction;
  instructionIndex: number;
}

/**
 * handle missing reference states for target transition states
 */
function validateTransitionStateReference({sequenceName, instruction, instructionIndex} : validateTransitionStateReferenceParams): ValidationError[] {
  const lStates = instruction.getParameter('states');
  // handle missing reference states for target transition states
  const lAllStateNames = lStates.map(pState => pState.stateName);
  return lStates
    .map(pState => pState.targetStates.map(pTargetState => ({state: pState, targetState: pTargetState})))
    .flat()
    .filter(pReference => !lAllStateNames.includes(pReference.targetState.stateName))
    .map(({state, targetState}) => {
      return {
        header: 'Missing associated State Machine state.',
        onError: {
          message: `"${sequenceName}" → command #${instructionIndex}, missing state "${targetState.stateName}" in the state transition from state "${state.stateName}" to state "${targetState.stateName}".`,
          options: [
            {message: 'Add a state to the State Machine command.', target: 'fsm_add_new_state'},
            {message: `Select a State from the drop down menu in the "${targetState.stateName}" modal.`, target: 'none'} // currently not handling highlighting the state select element in the modal
          ]
        },
        onSuccess: {
          message: 'You do not have any missing State Machine states.'
        },
        instructionSubResource: targetState
      };
    });
}

interface ValidateFsmNotInChildSequenceParams {
  instruction: Instruction;
}

/**
 * handle fsm instructions in a child sequence
 */
function validateFsmNotInChildSequence({ instruction } : ValidateFsmNotInChildSequenceParams): ValidationError[] {
  const lIsInInitialSequence = instruction.mSequence.isInitialSequence();
  if(lIsInInitialSequence) {
    return [];
  }

  return [
    {
      header: 'State Machine command in child sequence',
      onError: {
        message: 'It is recommended to have the State Machine command in the main sequence of the application.',
        options: [{ message: 'Move the State Machine command to the main sequence.', target: 'none' }] // there's going to be a button but it will not be associated to a target
      },
      onSuccess: {
        message: 'The State Machine command is in the main sequence.'
      }
    }
  ];
}


export function validateFsm(instruction: Instruction, appName: string, sequenceName: string, instructionIndex: number, configManager: ConfigManager): ValidationError[] {
  const lErrors: ValidationError[] = [];

  lErrors.push(...validateTransitionStateInputs({configManager, sequenceName, instruction, instructionIndex}));
  lErrors.push(...validateSequences(instruction, appName, sequenceName, instructionIndex));
  lErrors.push(...validateTransitionStateReference({sequenceName, instruction, instructionIndex}));
  lErrors.push(...validateFsmNotInChildSequence({instruction}));

  return lErrors;
}
